import { Dispatch } from "redux";
// @ts-ignore
import instance from "../../api/instance";
import {
  JobState,
  setJobError,
  setJobLoading,
  setJobListLoading, // NEW
  updateJobDetails,
  setJobList,
  setDraftsJobList,
  setDemoJobList,
  setClosedJobList,
  setRecentJobList,
  resetJobDraftState,
  setApplicantJobsList,
  setRecommendJobsList,
  setAppliedJobsList,
  setJobApplicantsList,
  setDraftJobsCount,
  setClosedJobsCount,
  setJobsCount,
  setTotalCount,
  setNextPage,
  setPreviousPage,
  setOptionsList,
  setCurrencies,
  setExperience,
  setInvitedJobList,
  setPendingInterviewJobList,
  setScheduledInterviewJobList,
  setInvitedJobsCount,
  setPendingInterviewJobsCount,
  setScheduledInterviewJobsCount,
  setRequestedJobList,
  setRequestedJobsCount,
  setDemoJobApplicantList,
  setAddonsList,
  setApplicationResult,
  setDraftLoading,
  setReportDetails,
  setAllCandidatesList,
  setAllCandidatesOptions,
  setAllCandidatesCount,
  updateJobApplication,
} from "../reducers/jobReducer";
// @ts-ignore
import { baseURL } from "../../api/instance";
import { JobDetails } from "../types/jobTypes";
import { toast } from "react-toastify";
// @ts-ignore
import {
  APPLICATION_STATUS_LABELS,
  APPLICATION_STAGE_LABELS,
  JOBS_LIST_PAGE_SIZE,
} from "../../utils/constants";
//@ts-ignore
import {
  createjob,
  updatejob,
  getJobDetails,
  checkjobStatus,
  searchJobs,
  recentJobList,
  jobListingJobSeeker,
  getJobApplications,
  applyjob,
  requestinterview,
  reportjob,
  jobInvite,
  getJobData,
  getJobApplicationDetails,
  applicationsListForDemo,
  listDemoInterviews,
  getAllJobApplications,
  getCompanyMyCandidates,
  manualApplicationFollowupStage2,
} from "../../api/apiRoutes";

/**
 * Manually trigger a stage-2 follow-up email for a job application (used by
 * the "Send Email" quick action on Qualified candidates). Job applications
 * only — lead candidates have no application_id. Backend enforces
 * owner/admin/recruiter permission and the candidate's unsubscribe
 * preference (both surfaced as 400s with a message).
 * Returns true on success so callers can react without reading the store.
 */
export const sendManualFollowupEmail =
  (token: string, applicationId: string | number) => async () => {
    if (!applicationId) {
      toast.error("Unable to send email: application not found.");
      return false;
    }
    try {
      const response = await instance.post(
        manualApplicationFollowupStage2,
        // Backend feeds application_id straight into Django's id__in=, so it
        // must be a list even for a single candidate (a bare string would be
        // iterated char-by-char and match nothing → 404).
        { application_id: [applicationId] },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      toast.success(response?.data?.message || "Follow-up email sent.");
      return true;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to send follow-up email. Please try again.",
      );
      return false;
    }
  };

/**
 * Bulk stage-2 follow-up: same endpoint as the single-send, but with an
 * `application_ids` array. Used by the "Send follow-up" button on the
 * Qualified view of view-jobs-data. Lead candidates have no application id
 * and must be filtered out by the caller.
 *
 * Returns the raw summary { sent_count, failed_count, sent, failed } so the
 * caller can render a per-candidate toast (partial success is normal — the
 * backend reports 200 even when some sends fail, e.g. unsubscribed
 * candidates). Returns null on a hard request failure.
 */
export const sendBulkFollowupEmail =
  (token: string, applicationIds: Array<string | number>) => async () => {
    const ids = (applicationIds || []).filter(
      (id) => id !== null && id !== undefined && id !== "",
    );
    if (ids.length === 0) {
      toast.error("Select at least one candidate to send a follow-up.");
      return null;
    }
    try {
      const response = await instance.post(
        manualApplicationFollowupStage2,
        // NOTE: the bulk endpoint reads the request key "application_id"
        // (singular) even though its value is an array of ids — matching the
        // backend's ManualFollowUpRateLimitedAPIView. Do not rename to
        // "application_ids" or the backend ignores it and 400s.
        { application_id: ids },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      return response?.data ?? null;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to send follow-up emails. Please try again.",
      );
      return null;
    }
  };

export const createJob =
  (
    token: string,
    jobDetails: JobState,
    status: string,
    navigate: Function,
    navigateTo?: string,
  ) =>
  async (
    dispatch: Dispatch,
    getState: () => { company?: { details?: { id?: string } } },
  ) => {
    if (status === "1") {
      dispatch(setDraftLoading(true));
    } else {
      dispatch(setJobLoading(true));
    }
    try {
      const companyId = getState?.()?.company?.details?.id;
      // create-job API expects client id only, not the whole client object
      const rawClient = (jobDetails as any)?.client;
      const normalizedClient =
        rawClient != null && typeof rawClient === "object"
          ? (rawClient.id ?? rawClient.user_id ?? null)
          : (rawClient ?? null);
      const jobDataWithStatus = {
        ...jobDetails,
        client: normalizedClient,
        status,
        // Manual job form sends salary_frequency; AI posting sends salary_period directly.
        // Fall back to the existing salary_period so it isn't clobbered to null.
        salary_period:
          (jobDetails as any).salary_frequency ??
          (jobDetails as any).salary_period ??
          null,
        ...(companyId != null && companyId !== "" && { company: companyId }),
      };

      const response = await instance.post(createjob, jobDataWithStatus, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        const job = {
          ...jobDetails,
          id: response.data.data.id,
          user: response.data.data.user,
          interview_time: response.data.data.interview_time,
        };
        dispatch(updateJobDetails(job));
        // Only navigate for draft jobs (status "1"), let caller handle navigation for posted jobs (status "2")
        if (status === "1") {
          navigate(navigateTo ?? "/dashboard");
        }
        dispatch(resetJobDraftState());
        dispatch(setJobLoading(false));
        dispatch(setDraftLoading(false));
      } else {
        dispatch(setJobLoading(false));
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
      return response.data;
    } catch (error: any) {
      // HA1-I2078: surface the server's real reason (validation/server error) so
      // callers (e.g. draft save) can show it instead of a generic message.
      // Resolved-value contract is unchanged: still resolves undefined on failure.
      const reason =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error.message;
      dispatch(setJobError(reason));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const updateJob =
  (
    token: string,
    jobId: number,
    jobDetails: JobDetails,
    navigate: Function,
    status?: string | number,
    location?: Location,
    navigateTo?: string,
    navigationState?: any,
  ) =>
  async (dispatch: Dispatch) => {
    if (status === "2" || status === 2) {
      dispatch(setDraftLoading(true));
    } else {
      dispatch(setJobLoading(true));
    }
    try {
      const jobDataWithStatus = {
        ...jobDetails,
        status,
        // AI posting sends salary_period directly; manual form sends salary_frequency.
        // Fall back to the existing salary_period so it isn't clobbered to null.
        salary_period:
          (jobDetails as any).salary_frequency ??
          (jobDetails as any).salary_period ??
          null,
      };

      const response = await instance.put(
        `${updatejob}/${jobId}/`,
        jobDataWithStatus,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );
      if (response.status === 200) {
        const job = {
          ...jobDetails,
          id: response.data.data.id,
          user: response.data.data.user,
        };
        dispatch(updateJobDetails(job));

        // Determine navigation target
        if (navigateTo) {
          // Use custom navigation target if provided
          navigate(navigateTo, {
            replace: true,
            state: navigationState || {},
          });
        } else if (location) {
          const draftJobEditRoute = `/dashboard/my-jobs/draftJobs/${jobId}/edit`;
          if (location.pathname.startsWith(draftJobEditRoute)) {
            // If posting a draft job, navigate to dashboard with share modal
            navigate("/dashboard", {
              state: { showShareJobModal: true, slug: response.data.data.slug },
              replace: true,
            });
          } else {
            // Determine job type from pathname or status
            let jobType = "myJobs"; // default
            if (location.pathname.includes("/draftJobs/")) {
              jobType = "draftJobs";
            } else if (location.pathname.includes("/closedJobs/")) {
              jobType = "closedJobs";
            } else if (location.pathname.includes("/inviteJobs/")) {
              jobType = "inviteJobs";
            } else if (status === "1" || status === 1) {
              jobType = "draftJobs";
            } else if (status === "3" || status === 3) {
              jobType = "closedJobs";
            }
            navigate(`/dashboard/my-jobs/${jobType}/${jobId}/view-jobs-data`, {
              replace: true,
              state: navigationState || {},
            });
          }
        } else {
          // Default: navigate to job details based on status
          let jobType = "myJobs"; // default
          if (status === "1" || status === 1) {
            jobType = "draftJobs";
          } else if (status === "3" || status === 3) {
            jobType = "closedJobs";
          }
          navigate(`/dashboard/my-jobs/${jobType}/${jobId}/view-jobs-data`, {
            replace: true,
            state: navigationState || {},
          });
        }

        dispatch(resetJobDraftState());
        dispatch(setJobLoading(false));
        dispatch(setDraftLoading(false));
        return response;
      } else {
        dispatch(setJobLoading(false));
        dispatch(setDraftLoading(false));
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      // HA1-I2078: surface the server's real reason (validation/server error) so
      // callers (e.g. draft save) can show it instead of a generic message.
      // Resolved-value contract is unchanged: still resolves undefined on failure.
      const reason =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error.message;
      dispatch(setJobError(reason));
      dispatch(setJobLoading(false));
      dispatch(setDraftLoading(false));
    } finally {
      dispatch(setJobLoading(false));
      dispatch(setDraftLoading(false));
    }
  };

export const fetchSingleJobDetails =
  (id: number, token?: string, excludeRelatedJobs?: boolean) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(`${getJobDetails}/${id}/`, {
        headers: {
          Authorization: token ? `Token ${token}` : "",
        },
      });
      if (response.status === 200) {
        let jobData = response.data.data;

        // If excludeRelatedJobs is true, remove the related_jobs field from the jobData
        if (excludeRelatedJobs) {
          delete jobData.related_jobs;
          delete jobData.field_of_study;
          delete jobData.responsibilities;
          delete jobData.add_ons;
          delete jobData.seniority_level;
        }

        dispatch(updateJobDetails(jobData)); // updateJobDetails action to update Redux state with job details
        dispatch(setJobLoading(false));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch job details: " + error.message)); // setJobError action for error handling
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const checkJobStatus = (id: number) => async (dispatch: Dispatch) => {
  dispatch(setJobLoading(true));
  try {
    const response = await instance.get(`${checkjobStatus}/${id}/`);

    if (response.status === 200) {
      dispatch(setJobLoading(false));
      return response.data.active;
    }
    dispatch(setJobLoading(false));
    return false;
  } catch (error: any) {
    console.error("Error checking job status:", error);
    dispatch(setJobError("Failed to check job status: " + error.message));
    dispatch(setJobLoading(false));
    return false;
  }
};

export const fetchJobsList =
  (
    token: string,
    search?: string,
    employment_id?: number,
    sort?: string,
    status?: number,
    currentPage?: number,
    pageSize: number = JOBS_LIST_PAGE_SIZE,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    const url = new URL(`${baseURL}${searchJobs}`);
    const params = new URLSearchParams();

    const page = currentPage || 1;
    const limit = pageSize;
    const offset = (page - 1) * pageSize;

    if (search) params.append("search", search);
    if (employment_id != null)
      params.append("employment_id", employment_id.toString());
    if (sort) params.append("sort", sort);
    if (status != null) params.append("status", status.toString());
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());
    params.append("page", page.toString());

    url.search = params.toString();

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        // Transform API response: rename applied_count to qualified_count and add lead_count
        const transformedData = response.data.data.map((job: any) => ({
          ...job,
          qualified_count: job.applied_count ?? job.qualified_count ?? 0,
          lead_count: job.lead_count ?? 0,
        }));

        // Expect server-side pagination: backend should honor `limit`/`offset` and return paginated rows.
        // Do NOT apply a length-based client-side slice heuristic here — it causes totals/pages desync
        // when the backend correctly implements pagination. If the backend returns more rows than
        // requested, log a warning so the backend owner can address it.
        const pageData = transformedData;
        if (transformedData.length > limit) {
          console.warn(
            `fetchJobsList: server returned ${transformedData.length} rows for limit=${limit}. Please enable server-side pagination.`,
          );
        }

        switch (status) {
          case 1:
            dispatch(setDraftsJobList(pageData));
            dispatch(setDraftJobsCount(response.data.count));
            break;
          case 3:
            dispatch(setClosedJobList(pageData));
            dispatch(setClosedJobsCount(response.data.count));
            break;
          case 7:
            dispatch(setInvitedJobList(pageData));
            dispatch(setInvitedJobsCount(response.data.count));
            break;
          default:
            dispatch(setJobList(pageData));
            dispatch(setJobsCount(response.data.count));
            break;
        }
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message)); // setJobError action for error handling
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchRecentJobsList =
  (token: string) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(recentJobList, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        // Transform API response: rename applied_count to qualified_count and add lead_count
        const transformedData = response.data.data.map((job: any) => ({
          ...job,
          qualified_count: job.applied_count ?? job.qualified_count ?? 0,
          lead_count: job.lead_count ?? 0,
        }));
        dispatch(setRecentJobList(transformedData));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchDemoJobsList =
  (token: string) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(listDemoInterviews, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(setDemoJobList(response.data.data));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchDemoJobsApplicantsList =
  (token: string) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(applicationsListForDemo, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(setDemoJobApplicantList(response.data.data));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

/**
 * UI status label(s) -> my-candidates `status` query value(s). Most statuses
 * go out as the UI label ("Shortlisted", "Under-Review"); these differ.
 * Accepts a single status or a comma-separated list.
 */
const STATUS_PARAM_OVERRIDES: Record<string, string> = {
  "Follow-up": "Follow_Up",
  Interviewed: "interviewed",
};

const toApiStatusParam = (status?: string | null): string =>
  status
    ? status
        .split(",")
        .map((s) => STATUS_PARAM_OVERRIDES[s.trim()] ?? s.trim())
        .join(",")
    : "";

export const fetchJobApplicants =
  (
    token: string,
    jobId: number,
    search?: string,
    sort?: string,
    status?: string,
    stage?: string,
    totalExperience?: string,
    noticePeriod?: string,
    location?: string,
    overallRating?: string,
    technicalSkillsRating?: string,
    problemSolvingRating?: string,
    communicationRating?: string,
    behaviouralRating?: string,
    minCurrentCtc?: number,
    maxCurrentCtc?: number,
    minExpectedCtc?: number,
    maxExpectedCtc?: number,
    currentPage?: number,
    pageSize: number = 12,
    assessmentStatus?: string,
    currency?: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    const url = new URL(`${baseURL}${getJobApplications}`);
    const params = new URLSearchParams();

    // Calculate limit and offset from page and pageSize
    const page = currentPage || 1;
    const limit = pageSize;
    const offset = (page - 1) * pageSize;

    // UI status label(s) -> API value(s); see toApiStatusParam
    const transformedStatus = toApiStatusParam(status);

    // Appending parameters
    params.append("job_id", jobId.toString());
    params.append("search", search ?? "");
    params.append("sort", sort ?? "");
    // Only append status if assessmentStatus is not provided
    // When assessment_status is present, don't send status parameter
    // By default, send status as empty string unless assessment_status is present
    if (!assessmentStatus) {
      params.append("status", transformedStatus);
    }
    params.append("stage", stage ?? "");
    params.append("total_experience", totalExperience ?? "");
    params.append("notice_period", noticePeriod ?? "");
    params.append("location", location ?? "");
    params.append("overall_rating", overallRating ?? "");

    params.append("technical_skills_rating", technicalSkillsRating ?? "");

    params.append("problem_solving_rating", problemSolvingRating ?? "");

    params.append("communication_rating", communicationRating ?? "");

    params.append("behavioural_rating", behaviouralRating ?? "");

    // Only append CTC parameters if they are defined (not undefined or null)
    if (minCurrentCtc != null) {
      params.append("min_current_ctc", minCurrentCtc.toString());
    }
    if (maxCurrentCtc != null) {
      params.append("max_current_ctc", maxCurrentCtc.toString());
    }
    if (minExpectedCtc != null) {
      params.append("min_expected_ctc", minExpectedCtc.toString());
    }
    if (maxExpectedCtc != null) {
      params.append("max_expected_ctc", maxExpectedCtc.toString());
    }
    if (currency) params.append("currency", currency);
    // Add assessment_status parameter if provided
    if (assessmentStatus) {
      params.append("assessment_status", assessmentStatus);
    }
    // Use limit and offset instead of page
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    url.search = params.toString(); // Complete URL encoding

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(setOptionsList(response.data.options));
        dispatch(setJobApplicantsList(response.data.data));
        dispatch(setJobsCount(response.data.count));
        return response.data.data;
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      console.error("Error fetching job applicants:", error);
      dispatch(setJobError("Failed to fetch job applicants: " + error.message));
      dispatch(setOptionsList({}));
      dispatch(setJobApplicantsList([]));
      dispatch(setJobsCount(0));
      return [];
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchAllCandidates =
  (
    token: string,
    userId: number,
    search?: string,
    sort?: string,
    status?: string,
    stage?: string,
    totalExperience?: string,
    relevantExperience?: string,
    noticePeriod?: string,
    location?: string,
    latestEmployment?: string,
    minCurrentCtc?: number,
    maxCurrentCtc?: number,
    minExpectedCtc?: number,
    maxExpectedCtc?: number,
    overallRating?: string,
    technicalSkillsRating?: string,
    problemSolvingRating?: string,
    communicationRating?: string,
    behaviouralRating?: string,
    currentPage?: number,
    pageSize: number = 12,
    jobStatus?: number,
    assessmentStatus?: string,
    currency?: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    // Build URL with userId in path
    const url = new URL(`${baseURL}${getAllJobApplications}`);
    const params = new URLSearchParams();

    // Calculate limit and offset from page and pageSize
    const page = currentPage || 1;
    const limit = pageSize;
    const offset = (page - 1) * pageSize;

    // UI status label(s) -> API value(s); see toApiStatusParam
    const transformedStatus = toApiStatusParam(status);

    // Append query parameters
    params.append("search", search ?? "");
    params.append("sort", sort ?? "");

    // CRITICAL: If assessment_status is provided, send it instead of status
    // When assessment_status is present, don't send status parameter
    if (assessmentStatus) {
      params.append("assessment_status", assessmentStatus);
      // Don't append status when assessment_status is present
    } else {
      params.append("status", transformedStatus);
    }

    params.append("stage", stage ?? "");
    params.append("total_experience", totalExperience ?? "");
    params.append("relevant_experience", relevantExperience ?? "");
    params.append("notice_period", noticePeriod ?? "");
    params.append("location", location ?? "");
    params.append("latest_employment", latestEmployment ?? "");
    params.append("min_current_ctc", minCurrentCtc?.toString() ?? "");
    params.append("max_current_ctc", maxCurrentCtc?.toString() ?? "");
    params.append("min_expected_ctc", minExpectedCtc?.toString() ?? "");
    params.append("max_expected_ctc", maxExpectedCtc?.toString() ?? "");
    params.append("overall_rating", overallRating ?? "");
    params.append("technical_skills_rating", technicalSkillsRating ?? "");
    params.append("problem_solving_rating", problemSolvingRating ?? "");
    params.append("communication_rating", communicationRating ?? "");
    params.append("behavioural_rating", behaviouralRating ?? "");
    if (jobStatus != null) params.append("job_status", jobStatus.toString());
    if (currency) params.append("currency", currency);
    // Use limit and offset instead of page
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    url.search = params.toString();

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        // Dispatch the response data to Redux store
        dispatch(setAllCandidatesOptions(response.data.options));
        dispatch(setAllCandidatesList(response.data.data));
        dispatch(setAllCandidatesCount(response.data.count));
        dispatch(setNextPage(response.data.next));
        dispatch(setPreviousPage(response.data.previous));
        return response.data.data;
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      console.error("Error fetching all candidates:", error);
      dispatch(setJobError("Failed to fetch all candidates: " + error.message));
      // Set empty defaults on error
      dispatch(setAllCandidatesOptions({}));
      dispatch(setAllCandidatesList([]));
      dispatch(setAllCandidatesCount(0));
      return [];
    } finally {
      dispatch(setJobLoading(false));
    }
  };

/** Fetch my-candidates filtered by client_id (job/company/my-candidates/?client_id=X). Used on View Client > Job Applicant tab. */
export const fetchCandidatesByClientId =
  (
    token: string,
    clientId: string | number,
    limit: number = 20,
    offset: number = 0,
    search?: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    const url = new URL(`${baseURL}${getCompanyMyCandidates}`);
    const params = new URLSearchParams();
    params.append("client_id", String(clientId));
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());
    if (search != null && search.trim()) params.append("search", search.trim());
    url.search = params.toString();
    try {
      const response = await instance.get(url.toString(), {
        headers: { Authorization: `Token ${token}` },
      });
      if (response.status === 200) {
        dispatch(setAllCandidatesOptions(response.data.options ?? {}));
        dispatch(setAllCandidatesList(response.data.data ?? []));
        dispatch(setAllCandidatesCount(response.data.count ?? 0));
        dispatch(setNextPage(response.data.next ?? null));
        dispatch(setPreviousPage(response.data.previous ?? null));
        return response.data;
      }
      throw new Error(`API responded with status: ${response.status}`);
    } catch (error: any) {
      console.error("Error fetching candidates by client:", error);
      dispatch(
        setJobError(
          "Failed to fetch candidates: " + (error?.message ?? "Unknown error"),
        ),
      );
      dispatch(setAllCandidatesOptions({}));
      dispatch(setAllCandidatesList([]));
      dispatch(setAllCandidatesCount(0));
      return { data: [], count: 0, options: {} };
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchJobsListJobSeeker =
  (
    token?: string,
    status?: number,
    search?: string,
    location?: string,
    sort?: string,
    work_mode?: number,
    max_salary?: number,
    min_salary?: number,
    experience_id?: number,
    limit?: number,
    offset?: number,
    short_description?: string,
    currency?: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobListLoading(true)); // Use jobListLoading

    const url = new URL(`${baseURL}${jobListingJobSeeker}`);
    const params = new URLSearchParams();

    if (search) params.append("search", search);
    if (status != null) params.append("status", status.toString());
    if (location != null) params.append("location", location.toString());
    if (sort) params.append("sort", sort);
    if (work_mode != null) params.append("work_mode", work_mode.toString());
    if (max_salary != null) params.append("max_salary", max_salary.toString());
    if (min_salary != null) params.append("min_salary", min_salary.toString());
    if (experience_id != null)
      params.append("experience_id", experience_id.toString());
    // if (currentPage != null) params.append("page", currentPage.toString());
    if (limit != null) params.append("limit", limit.toString());
    if (offset != null) params.append("offset", offset.toString());
    if (short_description)
      params.append("short_description", short_description);
    if (currency) params.append("currency", currency);

    url.search = params.toString();

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: token ? `Token ${token}` : "",
        },
      });

      if (response.status === 200) {
        switch (status) {
          case 1:
            dispatch(setApplicantJobsList(response.data.data.job_data));
            // Use filtered count for pagination
            if (typeof response.data.count === "number") {
              dispatch(setJobsCount(response.data.count));
            }
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            dispatch(setNextPage(response.data.next));
            dispatch(setPreviousPage(response.data.previous));
            break;
          case 2:
            dispatch(setAppliedJobsList(response.data.data.job_data));
            // Use filtered count for pagination
            if (typeof response.data.count === "number") {
              dispatch(setJobsCount(response.data.count));
            }
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            dispatch(setNextPage(response.data.next));
            dispatch(setPreviousPage(response.data.previous));
            break;
          case 3:
            dispatch(setRecommendJobsList(response.data.data.job_data));
            // Use filtered count for pagination
            if (typeof response.data.count === "number") {
              dispatch(setJobsCount(response.data.count));
            }
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            dispatch(setNextPage(response.data.next));
            dispatch(setPreviousPage(response.data.previous));
            break;
          case 4:
            dispatch(setPendingInterviewJobList(response.data.data.job_data));
            dispatch(setPendingInterviewJobsCount(response.data.count));
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            break;
          case 5:
            dispatch(setScheduledInterviewJobList(response.data.data.job_data));
            dispatch(setScheduledInterviewJobsCount(response.data.count));
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            break;
          // case 4:
          //   dispatch(setInvitedJobList(response.data.data.job_data));
          //   dispatch(setInvitedJobsCount(response.data.count));
          //   break;
          // case 5:
          //   dispatch(setRequestedJobList(response.data.data.job_data));
          //   dispatch(setRequestedJobsCount(response.data.count));
          //   break;
          default:
            dispatch(setApplicantJobsList(response.data.data.job_data));
            // Use filtered count for pagination
            if (typeof response.data.count === "number") {
              dispatch(setJobsCount(response.data.count));
            }
            dispatch(
              setTotalCount({
                total_all_jobs: response.data.data.total_all_jobs,
                total_applied_jobs: response.data.data.total_applied_jobs,
                total_recommended_jobs:
                  response.data.data.total_recommended_jobs,
                total_pending_interviews:
                  response.data.data.total_pending_interviews,
                total_scheduled_interviews:
                  response.data.data.total_scheduled_interviews,
              }),
            );
            dispatch(setNextPage(response.data.next));
            dispatch(setPreviousPage(response.data.previous));
            break;
        }
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobListLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobListLoading(false));
    } finally {
      dispatch(setJobListLoading(false));
    }
  };

export const applyJob =
  (
    token: string,
    jobId: number,
    status?: number,
    source?: number,
    reason?: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      // Build payload conditionally
      const payload: any = { job_id: jobId };
      if (status !== undefined) {
        payload.status = status;
      }
      if (source !== undefined) {
        payload.source = source;
      }
      if (reason !== undefined && reason !== null) {
        payload.reason = reason;
      }

      const response = await instance.post(applyjob, payload, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 201) {
        const interviewId = response.data.data; // Adjust according to your actual API response structure
        // Dispatch any necessary Redux actions here
        dispatch(setJobLoading(false));
        return interviewId;
      }
    } catch (error: any) {
      dispatch(setJobLoading(false));

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        // Use the message from the API response
        throw new Error(error.response.data.message);
      } else {
        // Fallback error message
        throw new Error("Failed to apply for the job.");
      }
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const requestInterview =
  (token: string, jobId: number) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      const response = await instance.post(
        requestinterview,
        { job_id: jobId },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 201) {
        const interviewRequestData = response.data.data;
        dispatch(setJobLoading(false));
        toast.success("Interview request sent successfully.");
        return interviewRequestData;
      }
    } catch (error: any) {
      dispatch(setJobLoading(false));

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
        throw new Error(error.response.data.message);
      } else {
        toast.error("Failed to request an interview.");
        throw new Error("Failed to request an interview.");
      }
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const closeJob =
  (
    token: string,
    jobId: number,
    statusString: string,
    jobDetails?: JobDetails,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    // Loaded on use: a static import made utils.js (and libphonenumber-js,
    // ~120 KB) part of the entry chunk. Callers already have it loaded.
    //@ts-ignore
    const { getStatusId } = await import("../../utils/utils");
    const statusId = getStatusId(statusString);

    if (!statusId) {
      dispatch(setJobError("Invalid job status"));
      dispatch(setJobLoading(false));
      throw new Error("Invalid job status");
    }

    try {
      // Build payload with status and interview_duration
      const payload: any = { status: statusId.toString() };

      // Include interview_duration if available in jobDetails
      if (
        jobDetails?.interview_duration !== undefined &&
        jobDetails?.interview_duration !== null
      ) {
        payload.interview_duration = jobDetails.interview_duration;
      }

      const response = await instance.put(`${updatejob}/${jobId}/`, payload, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch job applicants: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const updateJobApplicationStatus =
  (
    token: string,
    jobApplicationId: number,
    status: number | null,
    stage: number | null,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      // Build payload conditionally - only include fields that are not null
      const payload: any = {};
      if (status !== null) {
        payload.status = status;
      }
      if (stage !== null) {
        payload.stage = stage;
      }

      const response = await instance.put(
        `/jobseekers/update-job-application/${jobApplicationId}/`,
        payload,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 200) {
        // Update Redux store with response data (server response is authoritative)
        const updatedData: any = {};

        // Convert response data to display labels
        if (response.data?.status !== undefined) {
          // If response.status is a number (ID), convert to label
          if (typeof response.data.status === "number") {
            const statusLabel = Object.entries(APPLICATION_STATUS_LABELS).find(
              ([id]) => parseInt(id) === response.data.status,
            )?.[1];
            if (statusLabel) {
              updatedData.status = statusLabel;
            }
          } else {
            // If it's already a string, normalize "Follow_Up" to "Follow-up"
            let statusString = response.data.status;
            // if (statusString === "Follow_Up") {
            //   statusString = "Follow-up";
            // }
            updatedData.status = statusString;
          }
        }

        if (response.data?.stage !== undefined) {
          // If response.stage is a number (ID), convert to label
          if (typeof response.data.stage === "number") {
            const stageLabel = Object.entries(APPLICATION_STAGE_LABELS).find(
              ([id]) => parseInt(id) === response.data.stage,
            )?.[1];
            if (stageLabel) {
              updatedData.stage = stageLabel;
            }
          } else {
            // If it's already a string, use it directly
            updatedData.stage = response.data.stage;
          }
        }

        // If no response data, use the values we sent (convert IDs to labels)
        if (Object.keys(updatedData).length === 0) {
          if (status !== null) {
            // Convert status ID back to label for display
            const statusLabel = Object.entries(APPLICATION_STATUS_LABELS).find(
              ([id]) => parseInt(id) === status,
            )?.[1];
            if (statusLabel) updatedData.status = statusLabel;
          }
          if (stage !== null) {
            // Convert stage ID back to label for display
            const stageLabel = Object.entries(APPLICATION_STAGE_LABELS).find(
              ([id]) => parseInt(id) === stage,
            )?.[1];
            if (stageLabel) updatedData.stage = stageLabel;
          }
        }

        // Always dispatch update to ensure UI reflects changes
        dispatch(
          updateJobApplication({
            applicationId: jobApplicationId,
            ...updatedData,
          }),
        );

        toast.success("Updated successfully");
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to update: " + error.message));
      toast.error("Failed to update");
      dispatch(setJobLoading(false));
      throw error; // Re-throw for component error handling
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const reportJob =
  (token: string, message: string, jobId: number) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      const response = await instance.post(
        `${reportjob}/${jobId}/`,
        { message: message },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response) {
        dispatch(setJobLoading(false));
        return response;
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to report job: " + error.message));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const inviteCandidates =
  (token: string, jobId: number, emails: string[], slug?: string) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    const finalJobId = slug ? slug : jobId; //
    try {
      const response = await instance.post(
        `${jobInvite}/${finalJobId}/`,
        {
          emails: emails,
          status: 1,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      return response;
    } catch (error: any) {
      console.error("Error inviting candidates:", error);
      dispatch(setJobError("Failed to invite candidates: " + error.message));
      throw error;
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchCurrencies =
  (token: string) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(getJobData, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(setCurrencies(response.data.data.currency));
        dispatch(setExperience(response.data.data.experience));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchInvitedJobDetails =
  (id: number, token?: string) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(`/job/get-job-invitations/${id}/`, {
        headers: {
          Authorization: token ? `Token ${token}` : "",
        },
      });

      if (response.status === 200) {
        dispatch(setInvitedJobList(response.data.data));
        //dispatch(updateJobDetails(response.data.data));
        // updateJobDetails action to update Redux state with job details
        dispatch(setJobLoading(false));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch job details: " + error.message)); // setJobError action for error handling
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchInvitedApplicants =
  (
    token: string,
    jobId: number,
    search?: string,
    currentPage: number = 1,
    pageSize: number = 12,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    const url = new URL(`${baseURL}job/get-job-invitations/${jobId}/`);
    const params = new URLSearchParams();

    const page = currentPage || 1;
    const limit = pageSize;
    const offset = (page - 1) * pageSize;

    if (search) params.append("search", search);
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    url.search = params.toString();

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        const list = response?.data?.data ?? [];
        // API may ignore limit/offset and return the full list
        const pageData =
          list.length > limit ? list.slice(offset, offset + limit) : list;
        dispatch(setJobApplicantsList(pageData));
        dispatch(setJobsCount(response?.data?.count));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch job applicants: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchRequestedApplicants =
  (token: string, jobId: number, search?: string) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    const url = new URL(
      `${baseURL}job/company/requested-applicants-list/${jobId}/`,
    );
    const params = new URLSearchParams();

    if (search) params.append("search", search);

    url.search = params.toString();

    try {
      const response = await instance.get(url.toString(), {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(setJobApplicantsList(response?.data?.data));
        dispatch(setJobsCount(response?.data?.count));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch job applicants: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const RequestInterview =
  (token: string, jobId: number) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      await instance.post(
        requestinterview,
        { job_id: jobId },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
    } catch (error: any) {
      dispatch(setJobLoading(false));

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error("Failed to apply for the job.");
      }
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchDemoInterviewDetails = async (
  token: string,
  interviewId: number,
) => {
  try {
    const response = await instance.get(
      `/demo/get-demo-interview-details/${interviewId}/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );

    if (response.status === 200) {
      return response?.data?.data;
    } else {
      throw new Error(`API responded with status: ${response.status}`);
    }
  } catch (error) {
    throw new Error("Failed to fetch interview details");
  }
};

export const fetchAddons = (token: string) => async (dispatch: Dispatch) => {
  dispatch(setJobLoading(true));

  try {
    const response = await instance.get("/job/get-all-addons/", {
      headers: {
        Authorization: token ? `Token ${token}` : "",
      },
    });

    if (response.status === 200) {
      dispatch(setAddonsList(response.data.data));
      // updateJobDetails action to update Redux state with job details
      dispatch(setJobLoading(false));
    } else {
      throw new Error(`API responded with status: ${response.status}`);
    }
  } catch (error: any) {
    dispatch(setJobError("Failed to fetch job details: " + error.message)); // setJobError action for error handling
    dispatch(setJobLoading(false));
  } finally {
    dispatch(setJobLoading(false));
  }
};

export const updateAddon =
  (token: string, addonId: number, addonAdded: boolean) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));
    try {
      const response = await instance.put(
        `/job/update-addon/${addonId}/`,
        { addon_added: addonAdded },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(updateJobDetails(response.data.data));
        dispatch(setJobLoading(false));
        return response;
      } else {
        dispatch(setJobLoading(false));
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to update addon: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchJobApplicantsResult =
  (token: string, id: number) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(
        `${getJobApplicationDetails}/${id}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      if (response.status === 200) {
        dispatch(setApplicationResult(response.data.data));
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
      dispatch(setJobLoading(false));
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch jobs list: " + error.message));
      dispatch(setJobLoading(false));
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const fetchReportDetails =
  (token: string, id: number) => async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.get(
        `job/company/generate-pdf-report/${id}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );
      if (response.status === 200) {
        dispatch(setReportDetails(response.data)); // response.data is the report object
        dispatch(setJobLoading(false));
        return response.data; // Return the response data
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to fetch report details: " + error.message));
      dispatch(setJobLoading(false));
      throw error; // Re-throw the error so the component can handle it
    } finally {
      dispatch(setJobLoading(false));
    }
  };

export const resendJobInvite =
  (token: string, jobId: string, emails: string[]) =>
  async (dispatch: Dispatch) => {
    dispatch(setJobLoading(true));

    try {
      const response = await instance.post(
        `/job/job-invite/${jobId}/`,
        { emails },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setJobLoading(false));
        toast.success("Invites sent successfully!");
        return response.data;
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error: any) {
      dispatch(setJobError("Failed to resend invites: " + error.message));
      dispatch(setJobLoading(false));
      toast.error("Failed to send invites. Please try again.");
      throw error;
    } finally {
      dispatch(setJobLoading(false));
    }
  };
